import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { fetchApi } from "../api/client";
import { ScanLine, List, History, CheckCircle2, AlertTriangle, XCircle, Plus, ChevronLeft, MapPin } from "lucide-react";

const BarcodeScanner = lazy(() => import("../components/BarcodeScanner"));

function AssetScanner() {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth();
    const [tab, setTab] = useState("scan");
    const [stores, setStores] = useState([]);
    const [store, setStore] = useState(null);
    const [scanActive, setScanActive] = useState(true);
    const [scanResult, setScanResult] = useState(null);
    const [scanLoading, setScanLoading] = useState(false);
    const [assets, setAssets] = useState([]);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [assetsFilter, setAssetsFilter] = useState("all");
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [showBarcodeModal, setShowBarcodeModal] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [barcodeForm, setBarcodeForm] = useState({ barcode: "", description: "", classification: "" });
    const [barcodeFiles, setBarcodeFiles] = useState([]);
    const [barcodeSubmitting, setBarcodeSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [geoPosition, setGeoPosition] = useState(null);
    const [classifications, setClassifications] = useState([]);
    const [placementPreview, setPlacementPreview] = useState(null);

    // Auto-redirect single-site users
    useEffect(() => {
        if (siteId) return;
        const scopes = user?.scopes || [];
        const siteScope = scopes.find(s => s.type === "site");
        if (siteScope?.value) {
            const sites = siteScope.value.split(",").map(s => s.trim());
            if (sites.length === 1) {
                navigate(`/assets/${sites[0]}`, { replace: true });
            }
        }
    }, [siteId, user, navigate]);

    // Load store list for site selector — sorted by distance for non-site users
    useEffect(() => {
        if (siteId) return;
        const loadStores = async () => {
            try {
                let lat = geoPosition?.latitude;
                let lng = geoPosition?.longitude;
                const params = lat && lng ? `?lat=${lat}&lng=${lng}` : "";
                const data = await fetchApi(`/assets/nearby-sites${params}`);
                setStores(data.sites || []);
            } catch {
                // Fallback to generic stores endpoint
                try {
                    const data = await fetchApi("/stores");
                    setStores(data || []);
                } catch {}
            }
        };
        loadStores();
    }, [siteId, geoPosition]);

    // Load store info when siteId selected (use nearby-sites to avoid pages.stores permission)
    useEffect(() => {
        if (!siteId) { setStore(null); return; }
        fetchApi(`/assets/nearby-sites?`)
            .then(data => {
                const match = (data.sites || []).find(s => s.site_id === siteId);
                setStore(match || { site_id: siteId, name: `Site ${siteId}` });
            })
            .catch(() => setStore({ site_id: siteId, name: `Site ${siteId}` }));
    }, [siteId]);

    // Request geolocation
    useEffect(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            pos => setGeoPosition({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
            }),
            () => {} // ignore geo errors
        );
    }, []);

    // Load asset classifications (for barcode modal dropdown + placement photos)
    useEffect(() => {
        fetchApi("/assets/classifications")
            .then(data => setClassifications((data.classifications || []).filter(c => c.active)))
            .catch(() => {});
    }, []);

    // Load assets tab
    useEffect(() => {
        if (tab !== "assets" || !siteId) return;
        setAssetsLoading(true);
        fetchApi(`/assets/site/${siteId}`)
            .then(data => setAssets(data.assets || []))
            .catch(err => setError(err.message))
            .finally(() => setAssetsLoading(false));
    }, [tab, siteId]);

    // Load history tab
    useEffect(() => {
        if (tab !== "history" || !siteId) return;
        setHistoryLoading(true);
        fetchApi(`/assets/scan-history/${siteId}`)
            .then(data => setHistory(data.scans || []))
            .catch(err => setError(err.message))
            .finally(() => setHistoryLoading(false));
    }, [tab, siteId]);

    const handleScan = useCallback(async (barcode) => {
        if (!siteId || scanLoading) return;
        setScanLoading(true);
        setScanResult(null);
        setError(null);
        try {
            const data = await fetchApi("/assets/scan", {
                method: "POST",
                body: JSON.stringify({
                    siteId,
                    barcode,
                    latitude: geoPosition?.latitude,
                    longitude: geoPosition?.longitude,
                    accuracy: geoPosition?.accuracy,
                }),
            });
            setScanResult({ barcode, ...data });
            // Brief pause then re-enable scanner
            setScanActive(false);
            setTimeout(() => setScanActive(true), 1500);
        } catch (err) {
            setError(err.message);
        } finally {
            setScanLoading(false);
        }
    }, [siteId, scanLoading, geoPosition]);

    const handleBarcodeSubmit = async (e) => {
        e.preventDefault();
        setBarcodeSubmitting(true);
        try {
            const form = new FormData();
            form.append("siteId", siteId);
            if (barcodeForm.barcode) form.append("barcode", barcodeForm.barcode);
            if (selectedAsset) {
                form.append("mcAssetPk", selectedAsset.pk);
                form.append("mcAssetName", selectedAsset.name);
            }
            form.append("assetDescription", barcodeForm.description);
            form.append("classification", barcodeForm.classification || selectedAsset?.classification || "");
            if (geoPosition) {
                form.append("latitude", geoPosition.latitude);
                form.append("longitude", geoPosition.longitude);
            }
            for (const file of barcodeFiles) {
                form.append("photos", file);
            }

            const resp = await fetchApi("/assets/barcode-request", {
                method: "POST",
                headers: {}, // let browser set Content-Type for FormData
                body: form,
            });

            setShowBarcodeModal(false);
            setBarcodeForm({ barcode: "", description: "", classification: "" });
            setBarcodeFiles([]);
            setSelectedAsset(null);
            alert(resp.status === "approved"
                ? "Barcode assigned successfully."
                : "Barcode request submitted for review."
            );
        } catch (err) {
            setError(err.message);
        } finally {
            setBarcodeSubmitting(false);
        }
    };

    // Site selector
    if (!siteId) {
        const hasGeo = stores.some(s => s.distance_km != null);
        return (
            <div style={{ padding: 24, maxWidth: 600, margin: "0 auto" }}>
                <h2 style={{ marginBottom: 4 }}>Asset Scanner</h2>
                <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
                    {hasGeo
                        ? "Sites sorted by distance from your location."
                        : stores.length > 0 ? "Select a site to begin scanning." : "Loading sites..."}
                </p>
                {hasGeo && stores.length > 0 && stores[0].distance_km != null && stores[0].distance_km < 1 && (
                    <div style={{
                        background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8,
                        padding: "8px 12px", marginBottom: 12, fontSize: 13, color: "#166534",
                        display: "flex", alignItems: "center", gap: 6,
                    }}>
                        <MapPin size={14} />
                        You appear to be at or near <strong style={{ marginLeft: 2 }}>{stores[0].name}</strong>
                    </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {stores.map(s => (
                        <button
                            key={s.site_id}
                            onClick={() => navigate(`/assets/${s.site_id}`)}
                            style={{
                                padding: "12px 16px", textAlign: "left",
                                border: "1px solid #e2e8f0", borderRadius: 8,
                                background: "#fff", cursor: "pointer",
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}
                        >
                            <div>
                                <div style={{ fontWeight: 600 }}>{s.name}</div>
                                <div style={{ fontSize: 12, color: "#888" }}>
                                    {s.suburb}{s.state_code ? `, ${s.state_code}` : ""} — #{s.site_id}
                                </div>
                            </div>
                            {s.distance_km != null && (
                                <span style={{
                                    fontSize: 13, color: s.distance_km < 1 ? "#16a34a" : "#888",
                                    fontWeight: s.distance_km < 1 ? 600 : 400,
                                    whiteSpace: "nowrap", marginLeft: 12,
                                }}>
                                    {s.distance_km < 1
                                        ? `${Math.round(s.distance_km * 1000)}m`
                                        : `${s.distance_km.toFixed(1)}km`}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    const resultColors = {
        confirmed: { bg: "#f0fdf4", border: "#22c55e", icon: <CheckCircle2 size={24} color="#22c55e" /> },
        moved: { bg: "#fffbeb", border: "#f59e0b", icon: <AlertTriangle size={24} color="#f59e0b" /> },
        unknown: { bg: "#fef2f2", border: "#ef4444", icon: <XCircle size={24} color="#ef4444" /> },
        scanned: { bg: "#eff6ff", border: "#3b82f6", icon: <CheckCircle2 size={24} color="#3b82f6" /> },
    };

    const filteredAssets = assets.filter(a => {
        if (assetsFilter === "all") return true;
        if (assetsFilter === "scanned") return a.scanStatus === "confirmed";
        if (assetsFilter === "overdue") return a.scanStatus === "overdue";
        if (assetsFilter === "nobarcode") return !a.hasUpBarcode;
        return true;
    });

    return (
        <div style={{ padding: "16px 24px", maxWidth: 700, margin: "0 auto" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={() => navigate("/assets")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h2 style={{ margin: 0 }}>Asset Scanner</h2>
                    <span style={{ color: "#888", fontSize: 14 }}>{store?.name || siteId}</span>
                </div>
            </div>

            {error && (
                <div style={{ background: "#fef2f2", color: "#dc2626", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
                    {error}
                    <button onClick={() => setError(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer" }}>×</button>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "2px solid #e2e8f0", paddingBottom: 0 }}>
                {[
                    { key: "scan", label: "Scan", icon: <ScanLine size={16} /> },
                    { key: "assets", label: "Assets", icon: <List size={16} /> },
                    { key: "history", label: "History", icon: <History size={16} /> },
                ].map(t => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            padding: "8px 16px", border: "none", cursor: "pointer",
                            borderBottom: tab === t.key ? "2px solid #2563eb" : "2px solid transparent",
                            color: tab === t.key ? "#2563eb" : "#64748b",
                            fontWeight: tab === t.key ? 600 : 400,
                            background: "none", marginBottom: -2,
                        }}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* Scan tab */}
            {tab === "scan" && (
                <div>
                    <Suspense fallback={<div style={{ textAlign: "center", padding: 16, color: "#888" }}>Loading scanner...</div>}>
                        <BarcodeScanner onScan={handleScan} active={scanActive && !scanLoading} />
                    </Suspense>

                    {scanLoading && (
                        <div style={{ textAlign: "center", padding: 16, color: "#888" }}>Looking up barcode...</div>
                    )}

                    {scanResult && (
                        <div style={{
                            marginTop: 16, padding: 16, borderRadius: 8,
                            border: "2px solid #22c55e",
                            background: "#f0fdf4",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <CheckCircle2 size={24} color="#22c55e" />
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16 }}>Scanned</div>
                                    <div style={{ color: "#666", fontSize: 13 }}>{scanResult.barcode}{scanResult.asset ? ` — ${scanResult.asset.name}` : ""}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {hasPermission("assets.add") && (
                        <button
                            onClick={() => {
                                setBarcodeForm({ barcode: "", description: "", classification: "" });
                                setSelectedAsset(null);
                                setShowBarcodeModal(true);
                            }}
                            style={{
                                marginTop: 16, padding: "10px 16px", background: "#eff6ff",
                                color: "#2563eb", border: "1px solid #bfdbfe",
                                borderRadius: 8, cursor: "pointer", fontSize: 14,
                                fontWeight: 600, width: "100%",
                            }}
                        >
                            + Register New Asset
                        </button>
                    )}
                </div>
            )}

            {/* Assets tab */}
            {tab === "assets" && (
                <div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                        {[
                            { key: "all", label: "All" },
                            { key: "scanned", label: "Scanned" },
                            { key: "overdue", label: "Overdue" },
                            { key: "nobarcode", label: "No Barcode" },
                        ].map(f => (
                            <button
                                key={f.key}
                                onClick={() => setAssetsFilter(f.key)}
                                style={{
                                    padding: "6px 12px", borderRadius: 6, fontSize: 13,
                                    border: assetsFilter === f.key ? "1px solid #2563eb" : "1px solid #e2e8f0",
                                    background: assetsFilter === f.key ? "#eff6ff" : "#fff",
                                    color: assetsFilter === f.key ? "#2563eb" : "#64748b",
                                    cursor: "pointer",
                                }}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {assetsLoading ? (
                        <div style={{ textAlign: "center", padding: 32, color: "#888" }}>Loading assets...</div>
                    ) : filteredAssets.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 32, color: "#888" }}>
                            {assets.length === 0
                                ? "No assets registered for this site in MC."
                                : "No assets match this filter."}
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {filteredAssets.map(a => {
                                const statusColor = a.scanStatus === "confirmed" ? "#22c55e"
                                    : a.scanStatus === "overdue" ? "#f59e0b" : "#ef4444";
                                return (
                                    <div key={a.pk} style={{
                                        padding: "10px 14px", border: "1px solid #e2e8f0",
                                        borderRadius: 8, background: "#fff",
                                        borderLeft: `4px solid ${statusColor}`,
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.name}</div>
                                                <div style={{ fontSize: 13, color: "#888" }}>
                                                    {a.classification}
                                                    {a.hasUpBarcode ? ` | ${a.id}` : ""}
                                                    {a.model ? ` | ${a.model}` : ""}
                                                </div>
                                                {!a.hasUpBarcode && (
                                                    <div style={{ fontSize: 12, color: "#ef4444", marginTop: 2 }}>
                                                        No barcode
                                                    </div>
                                                )}
                                                {a.lastScanned && (
                                                    <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                                                        Scanned: {new Date(a.lastScanned).toLocaleDateString()}
                                                        {a.lastScannedBy ? ` by ${a.lastScannedBy}` : ""}
                                                    </div>
                                                )}
                                            </div>
                                            {!a.hasUpBarcode && hasPermission("assets.add") && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedAsset(a);
                                                        setBarcodeForm({ barcode: "", description: "", classification: a.classification || "" });
                                                        setShowBarcodeModal(true);
                                                    }}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 4,
                                                        padding: "4px 10px", fontSize: 12, background: "#eff6ff",
                                                        color: "#2563eb", border: "1px solid #bfdbfe",
                                                        borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <Plus size={14} /> Add Barcode
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* History tab */}
            {tab === "history" && (
                <div>
                    {historyLoading ? (
                        <div style={{ textAlign: "center", padding: 32, color: "#888" }}>Loading history...</div>
                    ) : history.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 32, color: "#888" }}>No scans recorded yet. Scan a barcode to start building the history.</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {history.map(s => (
                                <div key={s.id} style={{
                                    padding: "8px 12px", border: "1px solid #e2e8f0",
                                    borderRadius: 8, background: "#fff",
                                    borderLeft: "3px solid #22c55e",
                                    fontSize: 14,
                                }}>
                                    <div style={{ fontWeight: 600 }}>{s.barcode}{s.mc_asset_name ? ` — ${s.mc_asset_name}` : ""}</div>
                                    <div style={{ color: "#aaa", fontSize: 12 }}>
                                        {new Date(s.scanned_at).toLocaleString()}
                                        {s.scanned_by_name ? ` — ${s.scanned_by_name}` : ""}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* New Barcode Modal */}
            {showBarcodeModal && (
                <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
                }}>
                    <div style={{
                        background: "#fff", borderRadius: 12, padding: 24,
                        width: "100%", maxWidth: 440, maxHeight: "90vh", overflow: "auto",
                    }}>
                        <h3 style={{ margin: "0 0 16px" }}>
                            {selectedAsset ? `Add Barcode: ${selectedAsset.name}` : "Register New Asset"}
                        </h3>
                        <form onSubmit={handleBarcodeSubmit}>
                            <label style={labelStyle}>
                                Barcode
                                <input
                                    type="text"
                                    value={barcodeForm.barcode}
                                    onChange={e => setBarcodeForm({ ...barcodeForm, barcode: e.target.value })}
                                    placeholder="Scan or type barcode (e.g. UP1234567)"
                                    style={inputStyle}
                                />
                            </label>
                            {!selectedAsset && (
                                <>
                                    <label style={labelStyle}>
                                        Description
                                        <input
                                            type="text"
                                            value={barcodeForm.description}
                                            onChange={e => setBarcodeForm({ ...barcodeForm, description: e.target.value })}
                                            placeholder="What is this asset?"
                                            style={inputStyle}
                                        />
                                    </label>
                                    <label style={labelStyle}>
                                        Asset Type
                                        <select
                                            value={barcodeForm.classification}
                                            onChange={e => {
                                                const cls = classifications.find(c => c.classification_id === e.target.value);
                                                setBarcodeForm({ ...barcodeForm, classification: e.target.value });
                                                setPlacementPreview(cls || null);
                                            }}
                                            style={inputStyle}
                                        >
                                            <option value="">Select asset type...</option>
                                            {classifications.map(c => (
                                                <option key={c.classification_id} value={c.classification_id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </>
                            )}

                            {/* Placement photo + instructions */}
                            {(() => {
                                const cls = placementPreview || (selectedAsset?.classification
                                    ? classifications.find(c => c.name === selectedAsset.classification)
                                    : null);
                                if (!cls || (!cls.placement_photo && !cls.placement_notes)) return null;
                                return (
                                    <div style={{
                                        background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
                                        padding: 12, marginBottom: 12,
                                    }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1d4ed8", marginBottom: 6 }}>
                                            Barcode Placement — {cls.name}
                                        </div>
                                        {cls.placement_photo && (
                                            <img
                                                src={cls.placement_photo}
                                                alt={`${cls.name} barcode placement`}
                                                style={{
                                                    width: "100%", maxHeight: 200, objectFit: "contain",
                                                    borderRadius: 6, marginBottom: 6, background: "#fff",
                                                }}
                                            />
                                        )}
                                        {cls.placement_notes && (
                                            <div style={{ fontSize: 13, color: "#333" }}>
                                                {cls.placement_notes}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            <label style={labelStyle}>
                                Photos
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple
                                    onChange={e => setBarcodeFiles([...e.target.files])}
                                    style={inputStyle}
                                />
                            </label>
                            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                                <button
                                    type="submit"
                                    disabled={barcodeSubmitting || !barcodeForm.barcode}
                                    style={{
                                        flex: 1, padding: "10px 16px", background: "#2563eb", color: "#fff",
                                        border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
                                        opacity: barcodeSubmitting || !barcodeForm.barcode ? 0.5 : 1,
                                    }}
                                >
                                    {barcodeSubmitting ? "Submitting..." : "Submit"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowBarcodeModal(false); setSelectedAsset(null); }}
                                    style={{
                                        padding: "10px 16px", background: "#f1f5f9", color: "#333",
                                        border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

const labelStyle = { display: "block", marginBottom: 12, fontSize: 14, color: "#555", fontWeight: 500 };
const inputStyle = {
    display: "block", width: "100%", marginTop: 4, padding: "8px 10px",
    border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14,
    boxSizing: "border-box",
};

export default AssetScanner;
